import { Box } from '@mui/material';
import { DeliveryDateType, DocumentStatus, FunctionalAsset } from '@synergy/models';
import { Form, Formik, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PaloFormGrid } from '@synergy/components-palo';
import { PaloThemeProvider } from '@synergy/theme-palo';
import { ScrollToFieldError } from '@synergy/components';
import { connect } from 'react-redux';
import { postProcessMarkdown } from '@synergy/utils';

import { Account } from '../../../../../common/models/account';
import { AppState } from '../../../../../store/models/app-state';
import {
    FormControlledStepComponent,
    FormControlledStepComponentProps,
} from '../../../../../common/components/abstract/form-controlled-step-component';
import { PaloDirtyComparison } from '../../../../../common/components/palo/palo-dirty-context';
import { PaloOverviewSectionClassification } from './palo-section-classifications';
import { PaloOverviewSectionCustomer } from './palo-section-customer';
import { PaloOverviewSectionSupplier } from './palo-section-supplier';
import { PaloPositionsSlot } from '../../../../../common/components/palo/palo-positions-slot';
import { PaloPurchaseOrderHeadOrder } from './palo-section-order';
import { PaloPurchaseOrderSupplierWatcher } from '../../../palo/palo-purchase-order-supplier-watcher';
import { PaloRemarkPair } from '../../../../../common/components/palo/palo-remark-pair';
import { PurchaseOrder } from '../../../models/purchaseOrder';
import {
    PurchaseOrderDetailOverviewFormValues,
    purchaseOrderDetailOverviewFieldNames as fieldNames,
} from '../models/purchase-order-detail-form-values';
import { User } from '../../../../../common/models/user';
import { overviewValidationSchema } from '../overview-validation-schema';
import { pickCommonPartial } from '../../../../../common/utils/pick-common-partial';

/**
 * The head as one grid of seven rows: the order, the member, the member's
 * addresses, the member's order data, the supplier, delivery and terms. All 21
 * fields stay; labels sit beside them and the section titles go, so the
 * positions start about halfway down instead of below the fold (#2251).
 */
const PURCHASE_ORDER_HEAD_AREAS = [
    'title title orderDate responsibleContact',
    'customer customer customerContact avisoContact',
    'deliveryAddress deliveryAddress invoiceAddress invoiceAddress',
    'costCentre orderReference offerNumber chargingInvoiceAddress',
    'supplier supplier supplierContact functionalAsset',
    'deliveryDate deliveryDate paymentTerm classification',
    'termsOfDelivery termsOfDelivery termsOfDelivery supplierBonus',
];

/** The same head in three columns, for a narrow mask. */
const PURCHASE_ORDER_HEAD_NARROW_AREAS = [
    'title title responsibleContact',
    'customer customer orderDate',
    'customerContact customerContact avisoContact',
    'deliveryAddress deliveryAddress costCentre',
    'invoiceAddress invoiceAddress orderReference',
    'chargingInvoiceAddress chargingInvoiceAddress offerNumber',
    'supplier supplier supplierContact',
    'deliveryDate deliveryDate functionalAsset',
    'paymentTerm classification classification',
    'termsOfDelivery termsOfDelivery supplierBonus',
];

interface PaloPurchaseOrderDetailOverviewProps extends FormControlledStepComponentProps<Partial<PurchaseOrder>> {
    defaultCurrency: string;
    user: User;
    poolAccount: Account;
    functionalAsset: FunctionalAsset;
}

class PaloPurchaseOrderDetailOverviewBase extends FormControlledStepComponent<
    Partial<PurchaseOrder>,
    PurchaseOrderDetailOverviewFormValues,
    PaloPurchaseOrderDetailOverviewProps
> {
    protected initialValues: PurchaseOrderDetailOverviewFormValues = {
        sendPurchaseOrder: true,
        currency: this.props.defaultCurrency,
        responsibleContact: this.props.user.contact,
        functionalAsset: null,
        orderDate: new Date(),
        deliveryDate: undefined,
        deliveryDateType: DeliveryDateType.Deadline,
        title: '',

        customerAccount: undefined,
        customerContact: undefined,
        customerAvisoContact: undefined,
        deliveryAddress: undefined,
        invoiceAddress: undefined,
        chargingInvoiceAddress: undefined,
        costCentre: '',
        customerOrderNumber: '',
        orderReference: '',
        supplierOfferNumber: '',

        supplierAccount: undefined,
        supplierContact: undefined,
        // Listed so it survives the reduction below and reaches the form on a
        // document that is merely opened. It only ever got here when a supplier
        // was picked, which left the asset number greyed out and unmarked on
        // every saved purchase order — while the server refused to store one
        // without it.
        requireFunctionalAssets: false,
        paymentTerm: undefined,
        termsOfDeliveryContent: '',
        supplierBonus: '',
        noteToSupplier: '',
        classifications: [],

        internalNote: '',
        createdAt: new Date(),
    };

    /**
     * The shape this step hands to the wizard. Also what the unsaved marker
     * compares in, on both sides — the post-processing below would otherwise
     * read as a change on its own.
     */
    private toHandover = (values: PurchaseOrderDetailOverviewFormValues): Partial<PurchaseOrder> => ({
        ...pickCommonPartial(this.initialValues, values),
        noteToSupplier: postProcessMarkdown(values.noteToSupplier),
    });

    protected onSubmit = (values: PurchaseOrderDetailOverviewFormValues) => {
        this.props.propagateInternalState(this.toHandover(values));
    };

    private toFormValues(state?: Partial<PurchaseOrder>): PurchaseOrderDetailOverviewFormValues {
        // Allow all properties of the purchaseOrder partial, unused ones are ignored
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const definedState: any = {};

        Object.keys(state ?? {})
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .filter(key => (state as any)[key] != null)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .forEach(key => (definedState[key] = (state as any)[key]));

        // Reduced to the fields of this step, the same way onSubmit hands them
        // back. Otherwise the document's extra fields would make every
        // comparison against it fail.
        return { ...this.initialValues, ...pickCommonPartial(this.initialValues, definedState) };
    }

    private updateInternalValuesByProps(props: PaloPurchaseOrderDetailOverviewProps) {
        if (!props.internalState || !this.formikInstance) {
            return;
        }

        this.formikInstance.setValues(this.toFormValues(props.internalState));
    }

    public componentDidMount() {
        this.updateInternalValuesByProps(this.props);
    }

    public UNSAFE_componentWillReceiveProps = (nextProps: PaloPurchaseOrderDetailOverviewProps) => {
        if (this.props.internalState !== nextProps.internalState) {
            this.updateInternalValuesByProps(nextProps);
        }
    };

    public render() {
        const { unlocked } = this.props;

        const readonly =
            (!unlocked &&
                this.props.globalState &&
                this.props.globalState.status &&
                this.props.globalState.status !== DocumentStatus.Created) ||
            false;

        return (
            <PaloThemeProvider>
                <Box>
                    <Formik
                        initialValues={this.initialValues}
                        onSubmit={this.onSubmit}
                        innerRef={ref => (this.formikInstance = ref)}
                        validationSchema={overviewValidationSchema}
                        enableReinitialize={true}
                    >
                        {(formikBag: FormikProps<PurchaseOrderDetailOverviewFormValues>) => (
                            <Form>
                                <PaloDirtyComparison
                                    baseline={this.toHandover(this.toFormValues(this.props.documentState))}
                                    normalize={this.toHandover}
                                />
                                <ScrollToFieldError />
                                <PaloPurchaseOrderSupplierWatcher />
                                <PaloFormGrid
                                    columns={4}
                                    labels="side"
                                    areas={PURCHASE_ORDER_HEAD_AREAS}
                                    narrowAreas={PURCHASE_ORDER_HEAD_NARROW_AREAS}
                                >
                                    <PaloPurchaseOrderHeadOrder readonly={readonly} />
                                    <PaloOverviewSectionCustomer readonly={readonly} />
                                    <PaloOverviewSectionSupplier readonly={readonly} formikBag={formikBag} />
                                    <PaloOverviewSectionClassification />
                                </PaloFormGrid>
                                {/* What is ordered belongs under whom it is
                                    ordered from, not behind the remarks at the
                                    foot of the mask — the same place the
                                    invoice keeps it (#2208). */}
                                <PaloPositionsSlot />
                                <PaloRemarkPair
                                    external={{
                                        name: fieldNames.noteToSupplier,
                                        label: <FormattedMessage id="app.header.noteToSupplier" />,
                                    }}
                                    internalName={fieldNames.internalNote}
                                    readonly={readonly}
                                />
                            </Form>
                        )}
                    </Formik>
                </Box>
            </PaloThemeProvider>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    user: state.user,
    defaultCurrency: state.settings.currency,
    poolAccount: state.poolAccount,
});

export const PaloPurchaseOrderDetailOverview = connect(mapStateToProps)(PaloPurchaseOrderDetailOverviewBase);
